import React, { useMemo, useState } from "react";

const ENTITY_OPTIONS = ["VTPL", "RNS", "HUF"];
const inrFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

function apiUrl(path) {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL}${path}`;
}

const initialForm = {
  tenant_name: "",
  property_name: "",
  entity_name: "VTPL",
  advance_paid: "",
  advance_paid_date: "",
  receivable: "",
  received: "",
  dues: "",
  dues_month: "",
  remarks: ""
};

function parseDueMonthsCount(value) {
  if (!value) return 0;
  const directNumber = Number(value);
  if (!Number.isNaN(directNumber) && directNumber > 0) {
    return Math.floor(directNumber);
  }
  const matches = String(value).match(/\d+/);
  if (matches) return Number(matches[0]);
  return String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean).length;
}

const MONTH_INDEX = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  sept: 8,
  oct: 9,
  nov: 10,
  dec: 11
};

function parseDueMonthEntries(value) {
  if (!value) return [];
  const text = String(value).toLowerCase();
  const matches = [...text.matchAll(/([a-z]{3,9})(?:\s*&\s*([a-z]{3,9}))?\s*[-/ ]\s*(\d{4})/g)];
  const entries = [];

  matches.forEach((match) => {
    const year = Number(match[3]);
    const firstMonth = MONTH_INDEX[match[1]?.slice(0, 4).replace(".", "")];
    const secondMonth = match[2] ? MONTH_INDEX[match[2]?.slice(0, 4).replace(".", "")] : undefined;

    if (firstMonth !== undefined) {
      entries.push({ month: firstMonth, year });
    }
    if (secondMonth !== undefined) {
      entries.push({ month: secondMonth, year });
    }
  });

  return entries;
}

function getDueClass(value) {
  const parsedEntries = parseDueMonthEntries(value);

  if (parsedEntries.length > 0) {
    const now = new Date();
    const currentMonthNumber = now.getFullYear() * 12 + now.getMonth();
    const diffs = parsedEntries
      .map((entry) => currentMonthNumber - (entry.year * 12 + entry.month))
      .filter((diff) => diff >= 0);

    if (diffs.length > 0) {
      const maxDiff = Math.max(...diffs);
      if (maxDiff > 2) return "due-three-plus";
      if (maxDiff === 2) return "due-two";
      return "due-one";
    }
  }

  const months = parseDueMonthsCount(value);
  if (months > 2) return "due-three-plus";
  if (months === 2) return "due-two";
  if (months === 1) return "due-one";
  return "";
}

function formatDisplayDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
}

function formatDisplayDateList(value) {
  if (!value) return "-";
  const parts = String(value)
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

  if (parts.length === 0) return "-";

  return parts
    .map((item) => {
      const formatted = formatDisplayDate(item);
      return formatted === "-" ? item : formatted;
    })
    .join(", ");
}

function App() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeEntity, setActiveEntity] = useState("VTPL");
  const [activeSection, setActiveSection] = useState("financials");
  const [activeForm, setActiveForm] = useState("");
  const [selectedTenantId, setSelectedTenantId] = useState(null);
  const [propertyDetailPropertyId, setPropertyDetailPropertyId] = useState("");
  const [propertyDetailForm, setPropertyDetailForm] = useState({
    physical_address: "",
    google_map_location: "",
    building_type: "",
    sq_ft: "",
    other_comments: ""
  });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [loginError, setLoginError] = useState("");
  const [tenants, setTenants] = useState([]);
  const [properties, setProperties] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [newPropertyName, setNewPropertyName] = useState("");
  const [formData, setFormData] = useState(initialForm);
  const [editingTenantId, setEditingTenantId] = useState(null);
  const [generalMessage, setGeneralMessage] = useState("");
  const [uploadInfo, setUploadInfo] = useState({
    tenant_id: "",
    property_name: "",
    document_name: "",
    file: null
  });
  const [tenantDetailForm, setTenantDetailForm] = useState({
    tenant_id: "",
    tenant_email: "",
    tenant_contact_no: "",
    tenant_address: ""
  });

  const entityProperties = useMemo(
    () => properties.filter((item) => item.entity_name === activeEntity),
    [properties, activeEntity]
  );

  const groupedPropertyData = useMemo(
    () =>
      entityProperties.map((property) => ({
        ...property,
        tenants: tenants.filter((tenant) => tenant.property_name === property.property_name)
      })),
    [entityProperties, tenants]
  );

  const entityTotals = useMemo(() => {
    const totalReceivable = tenants.reduce((sum, tenant) => sum + Number(tenant.receivable || 0), 0);
    const totalReceived = tenants.reduce((sum, tenant) => sum + Number(tenant.received || 0), 0);
    return {
      totalReceivable,
      totalReceived,
      totalBalance: totalReceivable - totalReceived
    };
  }, [tenants]);

  const documentsByProperty = useMemo(() => {
    const grouped = {};
    documents.forEach((doc) => {
      const key = doc.property_name || "Unassigned";
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(doc);
    });
    return grouped;
  }, [documents]);

  const loadProperties = async () => {
    const response = await fetch(apiUrl("/properties"));
    const data = await response.json();
    setProperties(data);
  };

  const loadTenants = async (entity = activeEntity) => {
    const response = await fetch(apiUrl(`/tenants?entity_name=${encodeURIComponent(entity)}`));
    const data = await response.json();
    setTenants(data);
  };

  const loadDocuments = async (entity = activeEntity) => {
    const response = await fetch(apiUrl(`/documents?entity_name=${encodeURIComponent(entity)}`));
    const data = await response.json();
    setDocuments(data);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginError("");
    const response = await fetch(apiUrl("/login"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(loginForm)
    });
    const result = await response.json();
    if (!response.ok) {
      setLoginError(result.message || "Login failed");
      return;
    }
    setIsLoggedIn(true);
    await loadProperties();
    await loadTenants("VTPL");
    await loadDocuments("VTPL");
  };

  const resetTenantForm = () => {
    setFormData({ ...initialForm, entity_name: activeEntity, property_name: "" });
    setEditingTenantId(null);
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleTenantSubmit = async (event) => {
    event.preventDefault();
    setGeneralMessage("");
    const method = editingTenantId ? "PUT" : "POST";
    const url = editingTenantId ? `/tenants/${editingTenantId}` : "/tenants";
    const payload = {
      ...formData,
      entity_name: activeEntity,
      advance_paid: Number(formData.advance_paid || 0),
      receivable: Number(formData.receivable || 0),
      received: Number(formData.received || 0),
      dues: formData.dues === "" ? 0 : Number(formData.dues)
    };
    const response = await fetch(apiUrl(url), {
      method,
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save tenant");
      return;
    }
    setGeneralMessage(editingTenantId ? "Tenant updated" : "Tenant added");
    resetTenantForm();
    await loadTenants();
  };

  const startEditTenant = (tenant) => {
    setEditingTenantId(tenant.id);
    setFormData({
      tenant_name: tenant.tenant_name || "",
      property_name: tenant.property_name || "",
      entity_name: tenant.entity_name || activeEntity,
      advance_paid: tenant.advance_paid || "",
      advance_paid_date: tenant.advance_paid_date || "",
      receivable: tenant.receivable || "",
      received: tenant.received || "",
      dues: tenant.dues || "",
      dues_month: tenant.dues_month || "",
      remarks: tenant.remarks || ""
    });
    setActiveForm("add-tenant");
  };

  const handleDeleteTenant = async (tenantId) => {
    if (!window.confirm("Delete this tenant?")) return;
    await fetch(apiUrl(`/tenants/${tenantId}`), { method: "DELETE" });
    setGeneralMessage("Tenant deleted");
    await loadTenants();
  };

  const handleAddProperty = async (event) => {
    event.preventDefault();
    if (!newPropertyName.trim()) {
      setGeneralMessage("Enter property name");
      return;
    }
    const response = await fetch(apiUrl("/properties"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ entity_name: activeEntity, property_name: newPropertyName.trim() })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to add property");
      return;
    }
    setGeneralMessage("Property added");
    setNewPropertyName("");
    await loadProperties();
  };

  const handleUploadInput = (event) => {
    const { name, value, files } = event.target;
    if (name === "file") {
      setUploadInfo((prev) => ({ ...prev, file: files && files[0] ? files[0] : null }));
      return;
    }
    setUploadInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadInfo.file || !uploadInfo.property_name || !uploadInfo.document_name.trim()) {
      setGeneralMessage("Select property, document name and file first");
      return;
    }
    const body = new FormData();
    body.append("file", uploadInfo.file);
    body.append("tenant_id", uploadInfo.tenant_id);
    body.append("entity_name", activeEntity);
    body.append("property_name", uploadInfo.property_name);
    body.append("document_name", uploadInfo.document_name.trim());
    const response = await fetch(apiUrl("/upload"), { method: "POST", body });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Upload failed");
      return;
    }
    setGeneralMessage(`Uploaded: ${result.file_name}`);
    setUploadInfo({ tenant_id: "", property_name: "", document_name: "", file: null });
    await loadDocuments();
    const fileInput = document.getElementById("doc-file");
    if (fileInput) fileInput.value = "";
  };

  const handleEntityChange = async (entity) => {
    setActiveEntity(entity);
    setSelectedTenantId(null);
    setPropertyDetailPropertyId("");
    setEditingTenantId(null);
    setActiveForm("");
    setActiveSection("financials");
    setFormData((prev) => ({ ...prev, entity_name: entity, property_name: "" }));
    setUploadInfo({ tenant_id: "", property_name: "", document_name: "", file: null });
    setTenantDetailForm({
      tenant_id: "",
      tenant_email: "",
      tenant_contact_no: "",
      tenant_address: ""
    });
    await loadTenants(entity);
    await loadDocuments(entity);
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setActiveSection("financials");
    setActiveForm("");
    setLoginForm({ email: "", password: "" });
    setLoginError("");
    setGeneralMessage("");
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm("Delete this document?")) return;

    const response = await fetch(apiUrl(`/documents/${documentId}`), { method: "DELETE" });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to delete document");
      return;
    }

    setGeneralMessage("Document deleted");
    await loadDocuments();
  };

  const handleDownloadDocument = async (doc) => {
    const response = await fetch(apiUrl(`/documents/${doc.id}/download`));
    if (!response.ok) {
      setGeneralMessage("Unable to download document");
      return;
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = downloadUrl;
    anchor.download = doc.file_name || "document";
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.URL.revokeObjectURL(downloadUrl);
  };

  const handleTenantDetailSelection = (event) => {
    const tenantId = event.target.value;
    if (!tenantId) {
      setTenantDetailForm({
        tenant_id: "",
        tenant_email: "",
        tenant_contact_no: "",
        tenant_address: ""
      });
      return;
    }
    const selectedTenant = tenants.find((item) => String(item.id) === String(tenantId));
    if (!selectedTenant) return;
    setTenantDetailForm({
      tenant_id: String(selectedTenant.id),
      tenant_email: selectedTenant.tenant_email || "",
      tenant_contact_no: selectedTenant.tenant_contact_no || "",
      tenant_address: selectedTenant.tenant_address || ""
    });
  };

  const handleTenantDetailInput = (event) => {
    const { name, value } = event.target;
    setTenantDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveTenantDetails = async (event) => {
    event.preventDefault();
    if (!tenantDetailForm.tenant_id) {
      setGeneralMessage("Select a tenant first");
      return;
    }
    const selectedTenant = tenants.find(
      (item) => String(item.id) === String(tenantDetailForm.tenant_id)
    );
    if (!selectedTenant) {
      setGeneralMessage("Tenant not found");
      return;
    }
    const response = await fetch(apiUrl(`/tenants/${selectedTenant.id}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tenant_name: selectedTenant.tenant_name,
        property_name: selectedTenant.property_name,
        entity_name: selectedTenant.entity_name,
        advance_paid: selectedTenant.advance_paid,
        advance_paid_date: selectedTenant.advance_paid_date,
        receivable: selectedTenant.receivable,
        received: selectedTenant.received,
        dues: selectedTenant.dues,
        dues_month: selectedTenant.dues_month,
        remarks: selectedTenant.remarks,
        tenant_email: tenantDetailForm.tenant_email,
        tenant_contact_no: tenantDetailForm.tenant_contact_no,
        tenant_address: tenantDetailForm.tenant_address
      })
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save tenant details");
      return;
    }
    setGeneralMessage("Tenant details updated");
    await loadTenants();
  };

  const formatInr = (value) => inrFormatter.format(Number(value || 0));

  const openPropertyDetails = () => {
    setActiveForm(activeForm === "property-details" ? "" : "property-details");
  };

  const handlePropertySelection = (event) => {
    const propertyId = event.target.value;
    setPropertyDetailPropertyId(propertyId);
    const selected = entityProperties.find((item) => String(item.id) === String(propertyId));
    if (!selected) {
      setPropertyDetailForm({
        physical_address: "",
        google_map_location: "",
        building_type: "",
        sq_ft: "",
        other_comments: ""
      });
      return;
    }
    setPropertyDetailForm({
      physical_address: selected.physical_address || "",
      google_map_location: selected.google_map_location || "",
      building_type: selected.building_type || "",
      sq_ft: selected.sq_ft || "",
      other_comments: selected.other_comments || ""
    });
  };

  const handlePropertyDetailChange = (event) => {
    const { name, value } = event.target;
    setPropertyDetailForm((prev) => ({ ...prev, [name]: value }));
  };

  const savePropertyDetails = async () => {
    if (!propertyDetailPropertyId) {
      setGeneralMessage("Select a property first");
      return;
    }
    const response = await fetch(apiUrl(`/properties/${propertyDetailPropertyId}`), {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(propertyDetailForm)
    });
    const result = await response.json();
    if (!response.ok) {
      setGeneralMessage(result.message || "Unable to save property details");
      return;
    }
    setGeneralMessage("Property details updated");
    setPropertyDetailPropertyId("");
    setPropertyDetailForm({
      physical_address: "",
      google_map_location: "",
      building_type: "",
      sq_ft: "",
      other_comments: ""
    });
    await loadProperties();
  };

  if (!isLoggedIn) {
    return (
      <div className="login-wrapper">
        <form className="card login-card" onSubmit={handleLogin}>
          <h2>Rent Tracker Login</h2>
          <label>Email</label>
          <input
            type="email"
            value={loginForm.email}
            onChange={(e) => setLoginForm((prev) => ({ ...prev, email: e.target.value }))}
            required
          />
          <label>Password</label>
          <input
            type="password"
            value={loginForm.password}
            onChange={(e) => setLoginForm((prev) => ({ ...prev, password: e.target.value }))}
            required
          />
          {loginError && <p className="error">{loginError}</p>}
          <button type="submit">Login</button>
        </form>
      </div>
    );
  }

  return (
    <div className={`layout ${isSidebarCollapsed ? "sidebar-collapsed" : ""}`}>
      {isSidebarCollapsed && (
        <button
          className="sidebar-reopen-btn"
          type="button"
          onClick={() => setIsSidebarCollapsed(false)}
        >
          Show Menu
        </button>
      )}
      <aside className="sidebar">
        <button
          className="sidebar-toggle-btn"
          type="button"
          onClick={() => setIsSidebarCollapsed((prev) => !prev)}
        >
          {isSidebarCollapsed ? "Show Menu" : "Hide Menu"}
        </button>
        <h2>Rent Tracker</h2>
        {ENTITY_OPTIONS.map((entity) => (
          <button
            key={entity}
            className={activeEntity === entity ? "active" : ""}
            onClick={() => handleEntityChange(entity)}
          >
            {entity}
          </button>
        ))}
        <div className="sidebar-divider" />
        <button
          className={activeSection === "financials" ? "active" : ""}
          onClick={() => setActiveSection("financials")}
        >
          Financials
        </button>
        <button
          className={activeSection === "documents" ? "active" : ""}
          onClick={() => setActiveSection("documents")}
        >
          View Documents
        </button>
        <button
          className={activeSection === "tenant-details" ? "active" : ""}
          onClick={() => setActiveSection("tenant-details")}
        >
          Tenant Details
        </button>
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </aside>

      <main className="content">
        {generalMessage && <p className="info">{generalMessage}</p>}
        <h1>{activeEntity}</h1>
        {activeSection === "financials" && (
          <>
        <div className="cards">
          <div className="card stat-card">
            <p>Total Receivable</p>
            <h3>{formatInr(entityTotals.totalReceivable)}</h3>
          </div>
          <div className="card stat-card">
            <p>Total Received</p>
            <h3>{formatInr(entityTotals.totalReceived)}</h3>
          </div>
          <div className="card stat-card">
            <p>Total Balance</p>
            <h3>{formatInr(entityTotals.totalBalance)}</h3>
          </div>
        </div>

        <div className="card action-row">
          <button onClick={() => setActiveForm(activeForm === "add-property" ? "" : "add-property")}>
            Add Property
          </button>
          <button onClick={() => setActiveForm(activeForm === "add-tenant" ? "" : "add-tenant")}>
            Add Tenant
          </button>
          <button onClick={() => setActiveForm(activeForm === "upload-docs" ? "" : "upload-docs")}>
            Upload Documents
          </button>
          <button onClick={openPropertyDetails}>Property Details</button>
        </div>

        {activeForm === "add-property" && (
          <form className="card property-row" onSubmit={handleAddProperty}>
            <h3>Add Property</h3>
            <input
              placeholder={`Property name for ${activeEntity}`}
              value={newPropertyName}
              onChange={(e) => setNewPropertyName(e.target.value)}
            />
            <button type="submit">Add Property</button>
          </form>
        )}

        {activeForm === "add-tenant" && (
          <form className="card form-grid" onSubmit={handleTenantSubmit}>
            <h3>{editingTenantId ? "Edit Tenant" : "Add Tenant"}</h3>
            <input
              name="tenant_name"
              placeholder="Tenant Name"
              value={formData.tenant_name}
              onChange={handleInputChange}
              required
            />
            <select name="property_name" value={formData.property_name} onChange={handleInputChange} required>
              <option value="">Select Property</option>
              {entityProperties.map((item) => (
                <option key={item.id} value={item.property_name}>
                  {item.property_name}
                </option>
              ))}
            </select>
            <input name="entity_name" value={activeEntity} disabled />
            <input
              name="advance_paid"
              type="number"
              step="0.01"
              placeholder="Deposit"
              value={formData.advance_paid}
              onChange={handleInputChange}
            />
            <input
              name="advance_paid_date"
              type="text"
              placeholder="Deposit Dates (MM/DD/YYYY, MM/DD/YYYY)"
              value={formData.advance_paid_date}
              onChange={handleInputChange}
            />
            <input
              name="receivable"
              type="number"
              step="0.01"
              placeholder="Receivable"
              value={formData.receivable}
              onChange={handleInputChange}
            />
            <input
              name="received"
              type="number"
              step="0.01"
              placeholder="Received"
              value={formData.received}
              onChange={handleInputChange}
            />
            <input
              name="dues"
              type="number"
              step="0.01"
              placeholder="Dues"
              value={formData.dues}
              onChange={handleInputChange}
            />
            <input
              name="dues_month"
                placeholder="Due months (e.g., Mar-2026, Apr-2026)"
              value={formData.dues_month}
              onChange={handleInputChange}
            />
            <input
              name="remarks"
              placeholder="Remarks"
              value={formData.remarks}
              onChange={handleInputChange}
            />
            <div className="form-actions">
              <button type="submit">{editingTenantId ? "Update" : "Add"}</button>
              {editingTenantId && (
                <button type="button" className="secondary" onClick={resetTenantForm}>
                  Cancel
                </button>
              )}
            </div>
          </form>
        )}

        {activeForm === "upload-docs" && (
          <form className="card form-grid" onSubmit={handleUpload}>
            <h3>Upload Documents</h3>
            <select
              name="property_name"
              value={uploadInfo.property_name}
              onChange={handleUploadInput}
              required
            >
              <option value="">Select Property</option>
              {entityProperties.map((item) => (
                <option key={`upload-${item.id}`} value={item.property_name}>
                  {item.property_name}
                </option>
              ))}
            </select>
            <select name="tenant_id" value={uploadInfo.tenant_id} onChange={handleUploadInput}>
              <option value="">Select tenant (optional)</option>
              {tenants
                .filter((item) =>
                  uploadInfo.property_name ? item.property_name === uploadInfo.property_name : true
                )
                .map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.tenant_name}
                  </option>
                ))}
            </select>
            <input
              name="document_name"
              placeholder="Document Name"
              value={uploadInfo.document_name}
              onChange={handleUploadInput}
              required
            />
            <input
              id="doc-file"
              name="file"
              type="file"
              accept=".pdf,.doc,.docx,.xls,.xlsx"
              onChange={handleUploadInput}
              required
            />
            <button type="submit">Upload</button>
          </form>
        )}
        </>
        )}

        {activeSection === "documents" && (
          <div className="card">
            <h3>Documents by Property</h3>
            {Object.keys(documentsByProperty).length === 0 && <p>No documents uploaded yet.</p>}
            {Object.entries(documentsByProperty).map(([propertyName, propertyDocs]) => (
              <div key={propertyName} className="doc-property-block">
                <h4>{propertyName}</h4>
                <table className="rental-table">
                  <thead>
                    <tr>
                      <th>Document Name</th>
                      <th>File Name</th>
                      <th>Uploaded At</th>
                      <th>Download</th>
                      <th>Delete</th>
                    </tr>
                  </thead>
                  <tbody>
                    {propertyDocs.map((doc) => (
                      <tr key={doc.id}>
                        <td>{doc.document_name || "-"}</td>
                        <td>{doc.file_name}</td>
                        <td>{formatDisplayDate(doc.uploaded_at)}</td>
                        <td>
                          <button className="small-btn" onClick={() => handleDownloadDocument(doc)}>
                            Download
                          </button>
                        </td>
                        <td>
                          <button className="small-btn danger" onClick={() => handleDeleteDocument(doc.id)}>
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {activeSection === "tenant-details" && (
          <div className="card">
            <h3>Tenant Details by Property</h3>
            <form className="form-grid" onSubmit={saveTenantDetails}>
              <select
                name="tenant_id"
                value={tenantDetailForm.tenant_id}
                onChange={handleTenantDetailSelection}
                required
              >
                <option value="">Select Tenant</option>
                {tenants.map((tenant) => (
                  <option key={`tenant-detail-select-${tenant.id}`} value={tenant.id}>
                    {tenant.tenant_name} - {tenant.property_name}
                  </option>
                ))}
              </select>
              <input
                name="tenant_email"
                type="email"
                placeholder="Tenant Email"
                value={tenantDetailForm.tenant_email}
                onChange={handleTenantDetailInput}
              />
              <input
                name="tenant_contact_no"
                placeholder="Tenant Contact No"
                value={tenantDetailForm.tenant_contact_no}
                onChange={handleTenantDetailInput}
              />
              <input
                name="tenant_address"
                placeholder="Tenant Address"
                value={tenantDetailForm.tenant_address}
                onChange={handleTenantDetailInput}
              />
              <button type="submit">Save Tenant Details</button>
            </form>
            {groupedPropertyData.length === 0 && <p>No properties added yet.</p>}
            {groupedPropertyData.map((property) => (
              <div key={`tenant-detail-${property.id}`} className="doc-property-block">
                <h4>{property.property_name}</h4>
                <table className="rental-table">
                  <thead>
                    <tr>
                      <th>Tenant</th>
                      <th>Email</th>
                      <th>Contact No</th>
                      <th>Address</th>
                    </tr>
                  </thead>
                  <tbody>
                    {property.tenants.length === 0 && (
                      <tr>
                        <td colSpan="4">No tenants for this property.</td>
                      </tr>
                    )}
                    {property.tenants.map((tenant) => (
                      <tr key={`tenant-contact-${tenant.id}`}>
                        <td>{tenant.tenant_name}</td>
                        <td>{tenant.tenant_email || "-"}</td>
                        <td>{tenant.tenant_contact_no || "-"}</td>
                        <td>{tenant.tenant_address || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}

        {activeSection === "financials" && activeForm === "property-details" && (
          <form
            className="card form-grid"
            onSubmit={(event) => {
              event.preventDefault();
              savePropertyDetails();
            }}
          >
            <h3>Property Details</h3>
            <select value={propertyDetailPropertyId} onChange={handlePropertySelection} required>
              <option value="">Select Property</option>
              {entityProperties.map((item) => (
                <option key={`prop-details-${item.id}`} value={item.id}>
                  {item.property_name}
                </option>
              ))}
            </select>
            <input
              name="physical_address"
              placeholder="Physical Address"
              value={propertyDetailForm.physical_address}
              onChange={handlePropertyDetailChange}
            />
            <input
              name="google_map_location"
              placeholder="Google Map Location"
              value={propertyDetailForm.google_map_location}
              onChange={handlePropertyDetailChange}
            />
            <input
              name="building_type"
              placeholder="Building Type"
              value={propertyDetailForm.building_type}
              onChange={handlePropertyDetailChange}
            />
            <input
              name="sq_ft"
              placeholder="Sq Ft (if any)"
              value={propertyDetailForm.sq_ft}
              onChange={handlePropertyDetailChange}
            />
            <input
              name="other_comments"
              placeholder="Other Comments"
              value={propertyDetailForm.other_comments}
              onChange={handlePropertyDetailChange}
            />
            <button type="submit">Save Property Details</button>
          </form>
        )}

        {activeSection === "financials" && (
        <div className="card">
          <h3>All Properties and Rentals</h3>
          {groupedPropertyData.length === 0 && <p>No properties added yet.</p>}
          <div className="property-list-wrap">
            {groupedPropertyData.map((property) => (
              <div className="property-block" key={`prop-block-${property.id}`}>
                <div className="property-header">
                  <h4 className="property-title-static">{property.property_name}</h4>
                </div>

                <div className="table-card">
                  <table className="rental-table">
                    <thead>
                      <tr>
                        <th>Tenant</th>
                        <th>Receivable</th>
                        <th>Received</th>
                        <th>Balance</th>
                        <th>Dues</th>
                        <th>Due Month</th>
                        <th>Remarks</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {property.tenants.length === 0 && (
                        <tr>
                          <td colSpan="8">No tenants for this property.</td>
                        </tr>
                      )}
                      {property.tenants.map((tenant) => (
                        <React.Fragment key={tenant.id}>
                          <tr>
                            <td>
                              <button
                                className="tenant-link"
                                onClick={() =>
                                  setSelectedTenantId((prev) => (prev === tenant.id ? null : tenant.id))
                                }
                              >
                                {tenant.tenant_name}
                              </button>
                            </td>
                            <td>{formatInr(tenant.receivable)}</td>
                            <td>{formatInr(tenant.received)}</td>
                            <td>{formatInr(tenant.balance_receivable)}</td>
                            <td>{formatInr(tenant.dues)}</td>
                            <td className={getDueClass(tenant.dues_month)}>{tenant.dues_month || "-"}</td>
                            <td className="remarks-cell">{tenant.remarks || "-"}</td>
                            <td>
                              <button className="small-btn" onClick={() => startEditTenant(tenant)}>
                                Edit
                              </button>
                              <button className="small-btn danger" onClick={() => handleDeleteTenant(tenant.id)}>
                                Delete
                              </button>
                            </td>
                          </tr>
                          {selectedTenantId === tenant.id && (
                            <tr>
                              <td colSpan="8" className="tenant-detail">
                                <strong>Deposit:</strong> {formatInr(tenant.advance_paid)} |{" "}
                                <strong>Date(s):</strong> {formatDisplayDateList(tenant.advance_paid_date)}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}
      </main>
    </div>
  );
}

export default App;
